import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ImagePlus, X, PawPrint, Egg } from "lucide-react";
import { api, errText, photoUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { useLanguage } from "../../context/LanguageContext";
import { LITTER_SPECIES, kindFor, kindLabel, statusLabel, emptyLitter, speciesLabel } from "../../lib/litters";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { WaitingBoost } from "./WaitingBoost";

const Field = ({ label, children }) => <div><Label className="text-xs font-semibold text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;

const PhotoField = ({ value, onChange }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  if (value) return (
    <div className="relative h-28 w-40 rounded-xl overflow-hidden border border-slate-200">
      <img src={photoUrl(value)} alt="" className="h-full w-full object-cover" />
      <button type="button" onClick={() => onChange("")} data-testid="litter-photo-remove" className="absolute top-1.5 right-1.5 bg-black/55 text-white rounded-full p-1"><X size={12} /></button>
    </div>
  );
  return (
    <label className="h-28 w-40 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#F5551E] flex flex-col items-center justify-center gap-1 cursor-pointer text-xs text-slate-400" data-testid="litter-photo-upload">
      {busy ? <Loader2 size={18} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={18} />} {busy ? t("ltf_uploading") : t("ltf_add_photo")}
      <input type="file" accept="image/*" className="hidden" onChange={onFile} />
    </label>
  );
};

export function LitterFormDialog({ open, litter, onClose, onSaved }) {
  const { t, lang } = useLanguage();
  const [f, setF] = useState(emptyLitter);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(litter ? { ...emptyLitter, ...litter, expected_count: litter.expected_count ?? "", waitlist_cap: litter.waitlist_cap ?? "" } : emptyLitter);
  }, [open, litter]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const kind = f.kind || kindFor(f.species);
  const kindName = kindLabel(kind, t);
  const save = async () => {
    if (!f.breed.trim()) return toast.error(t("ltf_need_breed"));
    setSaving(true);
    const body = { ...f, kind, breed: f.breed.trim(), expected_count: f.expected_count ? Number(f.expected_count) : null, waitlist_cap: f.waitlist_cap ? Number(f.waitlist_cap) : null };
    ["id", "breeder_id", "created_at", "waitlist_count", "is_full", "my_entry", "waitlist"].forEach((k) => delete body[k]);
    try {
      if (litter) await api.put(`/my/litters/${litter.id}`, body);
      else await api.post("/my/litters", body);
      toast.success(litter ? t("ltf_saved") : `${kindName} — ${t("ltf_posted")}`);
      onSaved();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white" data-testid="litter-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{litter ? `${t("ltf_title_edit")} · ${kindName}` : t("ltf_title_new")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("ltf_desc")}</DialogDescription>
          <Link to="/litter-journey" target="_blank" data-testid="litter-form-journey-link" className="text-xs font-semibold text-[#F5551E] hover:underline">{lang === "fr" ? "Comment ça marche : de l'annonce aux photos des bébés →" : "How it works: from announcement to baby photos →"}</Link>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("ltf_species")}>
            <Select value={f.species} onValueChange={set("species")}>
              <SelectTrigger className="rounded-xl" data-testid="litter-species"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{LITTER_SPECIES.map((s) => <SelectItem key={s} value={s} data-testid={`litter-species-${s}`}>{speciesLabel(s, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("ltf_breed")}><Input value={f.breed} onChange={(e) => set("breed")(e.target.value)} maxLength={80} placeholder={t("ltf_breed_ph")} data-testid="litter-breed" className="rounded-xl" /></Field>
        </div>
        <div data-testid="litter-kind-picker">
          <p className="text-sm font-medium text-[#12234A] mb-1.5">{lang === "fr" ? "Type d'annonce" : "Posting type"}</p>
          <div className="grid grid-cols-2 gap-2">
            {[["litter", PawPrint, lang === "fr" ? "Portée" : "Litter", lang === "fr" ? "bébés nés vivants" : "live-born babies"], ["clutch", Egg, lang === "fr" ? "Couvée" : "Clutch", lang === "fr" ? "œufs à éclore" : "eggs to hatch"]].map(([k, Icon, label, sub]) => (
              <button key={k} type="button" onClick={() => set("kind")(k)} data-testid={`litter-kind-${k}`} aria-pressed={kind === k}
                className={`rounded-xl border-2 px-3 py-2.5 flex items-center gap-2.5 text-left transition-colors ${kind === k ? "border-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 hover:border-slate-300"}`}>
                <Icon size={18} className={kind === k ? "text-[#F5551E]" : "text-slate-400"} />
                <span><span className={`block text-sm font-semibold ${kind === k ? "text-[#F5551E]" : "text-[#12234A]"}`}>{label}</span><span className="block text-[11px] text-slate-500">{sub}</span></span>
              </button>
            ))}
          </div>
        </div>
        <WaitingBoost species={f.species} breed={f.breed} lang={lang} />
        <Field label={t("ltf_title")}><Input value={f.title} onChange={(e) => set("title")(e.target.value)} maxLength={100} placeholder={t("ltf_title_ph").replace("{kind}", kindName)} data-testid="litter-title" className="rounded-xl" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("ltf_status")}>
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger className="rounded-xl" data-testid="litter-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="upcoming">{statusLabel("upcoming", kind, t)}</SelectItem>
                <SelectItem value="arrived">{statusLabel("arrived", kind, t)}</SelectItem>
                <SelectItem value="closed">{t("ltf_closed_opt")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t(kind === "clutch" ? "ltf_count_chicks" : "ltf_count")}><Input type="number" min={1} max={40} value={f.expected_count} onChange={(e) => set("expected_count")(e.target.value)} data-testid="litter-count" className="rounded-xl" /></Field>
          <Field label={t(kind === "clutch" ? "ltf_hatch_date" : "ltf_due_date")}><Input type="date" value={f.expected_date} onChange={(e) => set("expected_date")(e.target.value)} data-testid="litter-expected-date" className="rounded-xl" /></Field>
          <Field label={t("ltf_ready")}><Input type="date" value={f.ready_date} onChange={(e) => set("ready_date")(e.target.value)} data-testid="litter-ready-date" className="rounded-xl" /></Field>
        </div>
        <Field label={t("ltf_price")}><Input value={f.price_note} onChange={(e) => set("price_note")(e.target.value)} maxLength={80} placeholder={t("ltf_price_ph")} data-testid="litter-price" className="rounded-xl" /></Field>
        <Field label={t("ltf_details")}><Textarea value={f.description} onChange={(e) => set("description")(e.target.value)} maxLength={1500} rows={3} placeholder={t("ltf_details_ph")} data-testid="litter-description" className="rounded-xl" /></Field>
        <Field label={t("ltf_photo")}><PhotoField value={f.photo} onChange={set("photo")} /></Field>
        <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
          <div><p className="text-sm font-semibold text-[#12234A]">{t("ltf_wl_open")}</p><p className="text-[11px] text-slate-500">{t("ltf_wl_open_sub")}</p></div>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={500} value={f.waitlist_cap} onChange={(e) => set("waitlist_cap")(e.target.value)} placeholder={t("ltf_no_limit")} data-testid="litter-cap" className="rounded-lg h-8 w-28 text-xs" />
            <Switch checked={f.waitlist_open} onCheckedChange={set("waitlist_open")} data-testid="litter-waitlist-open" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="litter-save" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">{saving && <Loader2 size={15} className="animate-spin" />} {litter ? t("ltf_save_changes") : `${t("ltf_post")} ${kindName.toLowerCase()}`}</Button>
      </DialogContent>
    </Dialog>
  );
}
